import React from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../Home/Navigation';
import { SEO } from '../SEO';
import seoConfig from '../SEO/seoConfig';
import styles from './AboutUs.module.css';

const AboutUs: React.FC = () => {
  const { t } = useTranslation('about');

  return (
    <div className={styles.container}>
      <SEO
        title={seoConfig.about.title}
        description={seoConfig.about.description}
        keywords={seoConfig.about.keywords}
        url="https://kingdomstudio.pro/about"
      />
      <Navigation />
      
      <div className={styles.content}>
        {/* Hero Section */}
        <section className={styles.hero}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>
              {t('about.hero.title')}
              <span className={styles.gradientText}> {t('about.hero.titleGradient')}</span>
            </h1>
            <p className={styles.heroSubtitle}>
              {t('about.hero.subtitle')}
            </p>
          </div>
        </section>

        {/* Vision Section */}
        <section className={styles.section}>
          <div className={styles.sectionContent}>
            <div className={styles.iconBox}>
              <span className={styles.icon}>{t('about.vision.icon')}</span>
            </div>
            <h2 className={styles.sectionTitle}>{t('about.vision.title')}</h2>
            <p className={styles.sectionText}>
              {t('about.vision.description')}
            </p>
          </div>
        </section>

        {/* Mission Section */}
        <section className={styles.section}>
          <div className={styles.sectionContent}>
            <div className={styles.iconBox}>
              <span className={styles.icon}>{t('about.mission.icon')}</span>
            </div>
            <h2 className={styles.sectionTitle}>{t('about.mission.title')}</h2>
            <p className={styles.sectionText}>
              {t('about.mission.description')}
            </p>
          </div>
        </section>

        {/* Journey Section */}
        <section className={styles.journeySection}>
          <h2 className={styles.journeyTitle}>{t('about.journey.title')}</h2>
          <div className={styles.journeyGrid}>
            <div className={styles.journeyCard}>
              <span className={styles.journeyIcon}>{t('about.journey.ideation.icon')}</span>
              <h3>{t('about.journey.ideation.title')}</h3>
              <p>{t('about.journey.ideation.description')}</p>
            </div>
            <div className={styles.journeyCard}>
              <span className={styles.journeyIcon}>{t('about.journey.creation.icon')}</span>
              <h3>{t('about.journey.creation.title')}</h3>
              <p>{t('about.journey.creation.description')}</p>
            </div>
            <div className={styles.journeyCard}>
              <span className={styles.journeyIcon}>{t('about.journey.production.icon')}</span>
              <h3>{t('about.journey.production.title')}</h3>
              <p>{t('about.journey.production.description')}</p>
            </div>
            <div className={styles.journeyCard}>
              <span className={styles.journeyIcon}>{t('about.journey.distribution.icon')}</span>
              <h3>{t('about.journey.distribution.title')}</h3>
              <p>{t('about.journey.distribution.description')}</p>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className={styles.valuesSection}>
          <h2 className={styles.valuesTitle}>{t('about.values.title')}</h2>
          <div className={styles.valuesGrid}>
            <div className={styles.valueCard}>
              <div className={styles.valueIcon}>{t('about.values.faithCentered.icon')}</div>
              <h3>{t('about.values.faithCentered.title')}</h3>
              <p>{t('about.values.faithCentered.description')}</p>
            </div>
            <div className={styles.valueCard}>
              <div className={styles.valueIcon}>{t('about.values.innovation.icon')}</div>
              <h3>{t('about.values.innovation.title')}</h3>
              <p>{t('about.values.innovation.description')}</p>
            </div>
            <div className={styles.valueCard}>
              <div className={styles.valueIcon}>{t('about.values.empowerment.icon')}</div>
              <h3>{t('about.values.empowerment.title')}</h3>
              <p>{t('about.values.empowerment.description')}</p>
            </div>
            <div className={styles.valueCard}>
              <div className={styles.valueIcon}>{t('about.values.community.icon')}</div>
              <h3>{t('about.values.community.title')}</h3>
              <p>{t('about.values.community.description')}</p>
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className={styles.storySection}>
          <div className={styles.storyContent}>
            <h2 className={styles.storyTitle}>{t('about.story.title')}</h2>
            <p className={styles.storyText}>
              {t('about.story.paragraph1')}
            </p>
            <p className={styles.storyText}>
              {t('about.story.paragraph2')}
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section className={styles.ctaSection}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>{t('about.cta.title')}</h2>
            <p className={styles.ctaText}>
              {t('about.cta.subtitle')}
            </p>
            <div className={styles.ctaButtons}>
              <button 
                className={styles.primaryButton}
                onClick={() => window.location.href = '/login-page?tab=signup'}
              >
                {t('about.cta.primaryButton')}
              </button>
              <button 
                className={styles.secondaryButton}
                onClick={() => window.location.href = '/features'}
              >
                {t('about.cta.secondaryButton')}
              </button>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className={styles.contactSection}>
          <h3 className={styles.contactTitle}>{t('about.contact.title')}</h3>
          <p className={styles.contactText}>
            {t('about.contact.subtitle')}
          </p>
          <a href={`mailto:${t('about.contact.email')}`} className={styles.contactButton}>
            <span className={styles.contactIcon}>{t('about.contact.icon')}</span>
            {t('about.contact.email')}
          </a>
        </section>
      </div>
    </div>
  );
};

export default AboutUs;
